import React, { useEffect, useState } from "react";
import ClientLayout from "../../components/client/ClientLayout";
import RequireClientAdmin from "../../components/client/RequireClientAdmin";
import SyncStatusBanner from "../../components/client/SyncStatusBanner";
import { clientApi } from "../../api";

/**
 * Data-quality gap finder. For each analysis code type configured for
 * the venue, shows how many members have a value populated vs. missing,
 * and the distribution of values across members.
 */
export default function AnalysisCoverageReport() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    clientApi.getAnalysisCoverage()
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Tag Coverage</h1>
          <p className="page-subtitle">For each venue-configured analysis tag (tier, club, preferences…), how many active members have a value set</p>
        </div>
      </div>

      <SyncStatusBanner cache={data?.cache} />

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Loading…</div>}

      {data && (
        <>
          <div className="stat-grid" style={{ marginBottom: "1.5rem" }}>
            <div className="stat-card">
              <span className="stat-label">Active members (sample)</span>
              <span className="stat-value">{data.total}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Tag types tracked</span>
              <span className="stat-value">{data.types.length}</span>
            </div>
          </div>

          {!data.types.length ? (
            <div className="empty-state">
              <h3>No tag types configured</h3>
              <p>Fetch the venue's analysis codes from S7000 first (Settings → Analysis Codes → Refresh from S7000). Analysis codes are venue-defined tags (tier, club, preferences, etc.).</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "1rem" }}>
              {data.types.map((t) => <TypeCard key={t.typecode} t={t} total={data.total} />)}
            </div>
          )}
        </>
      )}
    </>
  );
}

function TypeCard({ t }) {
  const coverage = Number(t.coverage) || 0;
  const color = coverage >= 80 ? "var(--success)" : coverage >= 40 ? "var(--warning)" : "var(--danger)";
  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <h3 style={{ marginBottom: 0 }}>{t.name}</h3>
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem" }}>Typecode <code>{t.typecode}</code></p>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
        <div style={{ fontSize: "2rem", fontWeight: 700, color }}>{coverage}%</div>
        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
          <div><strong>{t.withValue}</strong> with a value</div>
          <div><strong>{t.missing}</strong> missing</div>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--bg-elevated)", overflow: "hidden", marginBottom: "0.75rem" }}>
        <div style={{ height: "100%", width: `${coverage}%`, background: color, transition: "width 0.4s" }} />
      </div>
      {t.values.length ? (
        <div>
          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>Top values</div>
          {t.values.slice(0, 6).map((v) => (
            <div key={v.valuecode} style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", fontSize: "0.85rem" }}>
              <span>{v.label || <code>{v.valuecode}</code>}</span>
              <span style={{ color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{v.count}</span>
            </div>
          ))}
          {t.values.length > 6 && <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>+{t.values.length - 6} more</div>}
        </div>
      ) : <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No members have this code set.</div>}
    </div>
  );
}
